import React from "react";
import { Info, Search, Plus, Trash2 } from "lucide-react";
import MultitrackIcon from '../../../../../assets/images/icons/multitrack.svg';
import SettingsIcon from "../../../../../assets/images/icons/settings.svg";
import Dialog from "../../../../../components/Dialogbox";
import { useDispatch, useSelector } from "react-redux";
import MusicLoader from "../../../../../helper/MusicLoader";
import { PricingPlanModal } from "../../../../../components/PricingPlanModal";
import { showToast } from "../../../../../helper/ShowToast";
import Settings from "../../AudioEngineer/sections/Settings";
import { MixesTrackTable } from "./MixesTrackTable";
import { useMixesLibrary, useSearchMixesLibrary, useDeleteMixedTrack } from "../../../../../queries/urstudio/mixingengineer.queries";
import { uploadAndGetPreview, uploadData, uploadFileFromUrls } from "../../../../../redux/actions/audioEngineer";
import { useNavigate } from 'react-router-dom';
import { useDebounce } from "../../../../../hooks/useDebounce";

const MixesLibrary = ({ addNew, handleSettings, showHeader = true, setActiveTab }) => {
    const dispatch = useDispatch()
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);
    const [selectedTracks, setSelectedTracks] = React.useState([]);
    const [isMoving, setIsMoving] = React.useState(false);
    const [showPricingPlanModal, setShowPricingPlanModal] = React.useState(false);
    const localGenre = localStorage.getItem('genre')
    const localLoudness = localStorage.getItem('loudness')
    const localSampleRate = localStorage.getItem('sampleRate')
    const [genre, setGenre] = React.useState(localGenre || "ROCK_INDIE");
    const [loudness, setLoudness] = React.useState(localLoudness || "MEDIUM");
    const [sampleRate, setSampleRate] = React.useState(localSampleRate || "48000")
    const [query, setQuery] = React.useState("");
    const { hasActiveSubscription } = useSelector((state) => state.billing);

    const {
        data: library,
        isLoading,
    } = useMixesLibrary();

    const debouncedQuery = useDebounce(query, 500);
    const {
        data: searchResults,
        isLoading: isSearchLoading,
    } = useSearchMixesLibrary(debouncedQuery)

    const handleDialog = () => {
        setIsDialogOpen(!isDialogOpen);
    };

    const saveSettings = () => {
        if (!hasActiveSubscription) {
            showToast("info", "Please subscribe to change settings.");
            setIsDialogOpen(!isDialogOpen);
            setShowPricingPlanModal(true);   // Open subscription modal
            return;
        } else {
            setIsDialogOpen(!isDialogOpen);
            localStorage.setItem("genre", genre)
            localStorage.setItem("loudness", loudness)
            localStorage.setItem("sampleRate", sampleRate)
            handleSettings({ genre: genre, loudness: loudness })
        }
    }

    const deleteMutation = useDeleteMixedTrack();

    const handleBulkDelete = () => {
        if (!selectedTracks || selectedTracks.length === 0) return;
        const ids = selectedTracks.join(",");
        deleteMutation.mutate(ids, {
            onSuccess: () => {
                setSelectedTracks([]);
            }
        });
    };

    const navigate = useNavigate();
    const uploadedData = useSelector((state) => state.audioEngineer?.uploadedData);

    const handleMoveToMultitrack = () => {
        if (!selectedTracks || selectedTracks.length === 0) return;
        // show moving state immediately so button shows "Moving to Multitrack..."
        setIsMoving(true);
        // find selected track objects from current tracks array
        const itemsToMove = tracks.filter(t => selectedTracks.includes(t._id));
        if (itemsToMove.length === 0) {
            // try matching by id field differently
            const alt = tracks.filter(t => selectedTracks.includes(String(t.id)));
            if (alt.length > 0) itemsToMove.push(...alt);
        }

        // map to uploadedData expected shape
        const mapped = itemsToMove.map(item => ({
            _id: item._id || item.id,
            filename: item.filename || item.projectName || item.name,
            readable_url: item.readable_url || item.mixedUrl || item.fileUrl || item.originalUrl,
            originalUrl: item.mixedUrl
        }));

        // merge with existing uploadedData
        let merged = [];
        if (uploadedData) {
            if (Array.isArray(uploadedData.data)) merged = [...uploadedData.data];
            else if (Array.isArray(uploadedData)) merged = [...uploadedData];
        }
        mapped.forEach(m => {
            if (!merged.some(x => String(x._id) === String(m._id))) merged.push(m);
        });

        let requestBody;
        const urls = mapped.map(p => p.originalUrl).filter(Boolean);
        requestBody = {
            sourceUrls: urls
        };
        dispatch(uploadFileFromUrls(requestBody, setIsMoving, 'mixing-engineer', navigate, uploadedData?.data?.length > 0 ? true : false))
        setTimeout(() => {
            setActiveTab("multitrack")
        }, 3000)
    };

    // Clear selection once move completes (isMoving transitions true -> false)
    const prevIsMovingRef = React.useRef(false);
    React.useEffect(() => {
        if (prevIsMovingRef.current && !isMoving) {
            setSelectedTracks([]);
        }
        prevIsMovingRef.current = isMoving;
    }, [isMoving]);

    const handleSelectAll = () => {
        const libArr = Array.isArray(library) ? library : (library?.data || []);
        const searchArr = Array.isArray(searchResults) ? searchResults : (searchResults?.data || []);
        const current = debouncedQuery ? searchArr : libArr;
        const tracks = current || [];
        if (selectedTracks.length === tracks.length) {
            // Deselect all
            setSelectedTracks([]);
        } else {
            // Select all
            const allTrackIds = tracks.map(track => track._id);
            setSelectedTracks(allTrackIds);
        }
    };

    const libArr = Array.isArray(library) ? library : (library?.data || []);
    const searchArr = Array.isArray(searchResults) ? searchResults : (searchResults?.data || []);
    const tracks = debouncedQuery ? searchArr : libArr;
    const isAllSelected = tracks.length > 0 && selectedTracks.length === tracks.length;

    return (
        <div className="overflow-x-auto">
            {/* Header */}
            <header className="flex flex-wrap items-center gap-4 mb-4">
                {showHeader &&
                    (!isAllSelected && selectedTracks.length == 0 &&
                        <>
                            <Info size={24} className="text-theme_clr2" />
                            <h1 className="text-white text-2xl font-semibold">Mixes Library</h1>
                        </>
                    )
                }

                <div className="flex items-center gap-3 flex-wrap">
                    {/* Select All Button */}
                    {tracks.length > 0 && (
                        <button
                            onClick={handleSelectAll}
                            className="bg-theme_clr2 hover:bg-theme_clr4 text-white px-4 py-2 rounded-full flex items-center gap-2 transition-colors text-sm"
                        >
                            <span>{isAllSelected ? 'Deselect All' : 'Select All'}</span>
                        </button>
                    )}

                    {/* Show storage or action buttons based on selection */}
                    {selectedTracks.length > 0 ? (
                        <>
                            <button
                                onClick={handleBulkDelete}
                                disabled={deleteMutation.isLoading}
                                className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white px-3 py-2 rounded-full flex items-center gap-2 transition-colors text-sm"
                            >
                                <Trash2 size={16} />
                                <span>Delete</span>
                            </button>

                            <button
                                onClick={handleMoveToMultitrack}
                                disabled={isMoving}
                                className="bg-white text-theme_clr2 font-semibold px-3 py-2 rounded-full flex items-center gap-2 transition-colors text-sm"
                            >
                                <img src={MultitrackIcon} alt="Icon" />
                                <span>{isMoving ? "Moving to Multitrack..." : "Move to Multitrack"}</span>
                            </button>

                            <div className="text-gray-300 text-sm ml-1">
                                {selectedTracks.length} selected
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center gap-2 text-gray-300 text-sm">
                            <span>Storage:</span>
                            <span className="font-medium">{library?.storage?.totalFinalizedMixesStorage} MB used</span>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-3 flex-wrap ml-auto">
                    {/* Search bar */}
                    <div className="relative w-full md:w-[320px]">
                        <Search
                            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                            size={18}
                        />
                        <input
                            type="text"
                            placeholder="Search by name"
                            onChange={(e) => setQuery(e.target.value)}
                            className="bg-transparent border border-gray-400 text-white text-sm rounded-full pl-12 pr-6 py-2 w-full"
                        />
                    </div>

                    {/* Upload Button */}
                    <button
                        onClick={addNew}
                        className="bg-theme_clr2 hover:bg-theme_clr4 text-white px-4 py-2 rounded-full flex items-center gap-2 transition-colors text-sm"
                    >
                        <Plus size={20} />
                        <span>Upload</span>
                    </button>

                    {/* Settings Button */}
                    <button
                        onClick={handleDialog}
                        className="bg-white flex items-center justify-center rounded-full hover:bg-theme_clr2 h-[35px] w-[35px] transition-colors"
                    >
                        <img src={SettingsIcon} className="block" alt="Settings" />
                    </button>
                </div>
            </header>
            {(debouncedQuery ? isSearchLoading : isLoading) ?
                <MusicLoader /> :
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm border-separate border-spacing-y-2">
                        <MixesTrackTable
                            key={selectedTracks}
                            selectedTracks={selectedTracks}
                            setSelectedTracks={setSelectedTracks}
                            libraryData={tracks || []}
                            setActiveTab={setActiveTab}
                        />
                    </table>
                </div>
            }
            {/* Settings Dialog */}
            <Dialog title={"Settings"} hasActiveSubscription={hasActiveSubscription} isOpen={isDialogOpen} onClose={handleDialog} onSave={saveSettings} leftButton={'Cancel'} rightButton={'Save'}>
                <hr className="border-gray-700 mb-3" />
                {/* Mastering Preferences */}
                {!hasActiveSubscription &&
                    <div className="mb-6">
                        <h3 className="font-medium text-theme_clr2 mb-3">
                            Mastering Preferences
                        </h3>
                        <div className="bg-theme_clr1 text-gray-300 p-4 rounded-xl flex flex-col gap-3">
                            <p>
                                <span className="mr-2">👑</span> Save your settings and speed up
                                your workflow with a subscription.
                            </p>
                            <button className="bg-white text-[#0B1E3F] px-4 py-2 rounded-lg font-medium w-fit">
                                Upgrade now
                            </button>
                        </div>
                    </div>
                }
                <Settings
                    genre={genre}
                    setGenre={setGenre}
                    // loudness={loudness}
                    // setLoudness={setLoudness}
                    sampleRate={sampleRate}
                    setSampleRate={setSampleRate}
                    showLoudness={false}
                />

                <hr className="border-gray-700" />
            </Dialog>
            <PricingPlanModal isOpen={showPricingPlanModal} onClose={() => setShowPricingPlanModal(false)} returnUrl={window.location.origin + "/admin/audio-engineer"} />
        </div>
    );
};

export default MixesLibrary;
